import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Pencil, Search, ShieldAlert, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { errorCodeOf } from '../../services/api-error';
import {
  trophyParticipantsService,
  type TrophyParticipant,
} from '../../services/trophyParticipants';
import type { RegistrationCategory, RegistrationTeam } from '../../services/registrations';
import TrophyParticipantDialog from '../components/TrophyParticipantDialog';
import { useCurrentLocation } from '../hooks/useCurrentLocation';

/**
 * Everybody taking part in the circuit, gathered across its stages.
 *
 * **Not a roster** — a trophy keeps none, because one record of a person is
 * enough and the entry already is one. This is the circuit's view of what its
 * stages hold: one row per person, with the stages they are in beside them.
 *
 * Correcting somebody here writes to every entry they have in the circuit,
 * which is the reason to have this page at all: a tax code is fixed once rather
 * than in six places. The payment is deliberately not here — money is taken at
 * a stage's desk, for that stage, and settling six of them from one screen
 * would hide which.
 */
export default function TrophyParticipantsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { location: trophy, loading: trophyLoading, slug } = useCurrentLocation();

  const [participants, setParticipants] = useState<TrophyParticipant[]>([]);
  const [categories, setCategories] = useState<RegistrationCategory[]>([]);
  const [teams, setTeams] = useState<RegistrationTeam[]>([]);
  // Whether the form asks the gender of a person's class: the circuit's
  // agreement covers it.
  const [genderCollected, setGenderCollected] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [dpaRequired, setDpaRequired] = useState(false);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<TrophyParticipant | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(
    async (trophySlug: string) => {
      setListLoading(true);
      try {
        const page = await trophyParticipantsService.list(trophySlug);
        setParticipants(page.participants);
        setCategories(page.categories);
        setTeams(page.teams);
        setGenderCollected(page.genderCollected);
        setDpaRequired(false);
      } catch (error) {
        // Not signing the Art. 28 agreement is not a failure to load: the
        // server is refusing to serve people it has no contract to hold, and
        // the admin needs the way out rather than a red toast.
        if (errorCodeOf(error) === 'dpa_required') {
          setDpaRequired(true);
          return;
        }
        toast({
          title: t('gymAdmin.trophyParticipants.loadError'),
          description: error instanceof Error ? error.message : t('errors.unexpectedError'),
          variant: 'destructive',
        });
      } finally {
        setListLoading(false);
      }
    },
    [t, toast]
  );

  useEffect(() => {
    if (slug) load(slug);
  }, [slug, load]);

  // A circuit's entry list is a few hundred people at most, and it arrives in
  // one response — so the search is here rather than a round trip.
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return participants;

    return participants.filter((person) =>
      [person.full_name, person.email, person.guardian_email, person.team_name]
        .filter(Boolean)
        .some((field) => field!.toLowerCase().includes(term))
    );
  }, [participants, search]);

  const handleRemove = async (person: TrophyParticipant) => {
    if (!slug) return;
    const message = t('gymAdmin.trophyParticipants.confirmRemove', {
      name: person.full_name,
      count: person.stages_count,
    });
    if (!window.confirm(message)) return;

    setBusyId(person.id);
    try {
      await trophyParticipantsService.remove(slug, person.id);
      setParticipants((current) => current.filter((p) => p.id !== person.id));
    } catch (error) {
      toast({
        title: t('gymAdmin.trophyParticipants.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleSaved = (saved: TrophyParticipant) =>
    setParticipants((current) => current.map((p) => (p.id === saved.id ? saved : p)));

  if (trophyLoading) return <Skeleton className="h-96 w-full max-w-5xl" />;
  if (!trophy || !slug) return null;

  if (dpaRequired) {
    return (
      <Card className="max-w-3xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldAlert className="size-5 text-destructive" />
            {t('gymAdmin.registrations.dpaTitle')}
          </CardTitle>
          <CardDescription>{t('gymAdmin.trophyParticipants.dpaDescription')}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="max-w-5xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('gymAdmin.trophyParticipants.title')}</CardTitle>
          <CardDescription>
            {t('gymAdmin.trophyParticipants.summary', { count: participants.length })}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('gymAdmin.trophyParticipants.searchPlaceholder')}
              className="pl-9"
            />
          </div>

          {listLoading && <Skeleton className="h-24 w-full" />}

          {!listLoading && visible.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {participants.length === 0
                ? t('gymAdmin.trophyParticipants.empty')
                : t('gymAdmin.trophyParticipants.noMatch')}
            </p>
          )}

          {!listLoading &&
            visible.map((person) => (
              <div
                key={person.id}
                className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-start sm:justify-between"
                data-testid="trophy-participant-row"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-medium">{person.full_name}</span>
                    {person.category_name && (
                      <Badge variant="outline">{person.category_name}</Badge>
                    )}
                    {person.minor && (
                      <Badge
                        variant="outline"
                        className="font-normal"
                        title={t('gymAdmin.registrations.minorHint')}
                      >
                        {t('gymAdmin.registrations.minor')}
                      </Badge>
                    )}
                    {person.managed && (
                      <Badge variant="secondary" className="font-normal">
                        {t('gymAdmin.registrations.managed')}
                      </Badge>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
                    {person.team_name && <span className="truncate">{person.team_name}</span>}
                    {person.email && <span className="truncate">{person.email}</span>}
                    {/* The address the letter went to, for a child. */}
                    {person.guardian_email && (
                      <span className="truncate">
                        {t('gymAdmin.registrations.guardianEmailShort', { email: person.guardian_email })}
                      </span>
                    )}
                  </div>

                  {/* Which stages, and which are settled — the reason this view
                      exists rather than six separate lists. */}
                  <div className="flex flex-wrap gap-1 pt-1">
                    {person.stages.map((stage) => (
                      <Badge
                        key={stage.entry_id}
                        variant={stage.paid ? 'default' : 'secondary'}
                        className="font-normal"
                      >
                        {stage.stage_name}
                        {stage.paid ? ' ✓' : ''}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="flex shrink-0 gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setEditing(person);
                      setDialogOpen(true);
                    }}
                    title={t('gymAdmin.trophyParticipants.edit')}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={busyId === person.id}
                    onClick={() => handleRemove(person)}
                    title={t('gymAdmin.trophyParticipants.remove')}
                  >
                    {busyId === person.id ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Trash2 className="size-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
        </CardContent>
      </Card>

      <TrophyParticipantDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        trophySlug={slug}
        participant={editing}
        categories={categories}
        teams={teams}
        genderCollected={genderCollected}
        onTeamCreated={(team) =>
          setTeams((current) =>
            current.some((existing) => existing.id === team.id)
              ? current
              : [ ...current, team ].sort((a, b) => a.name.localeCompare(b.name))
          )
        }
        onSaved={handleSaved}
      />
    </div>
  );
}
